import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import {
  WorkspaceAccessibilityLevel,
  WorkspaceAccessibilityResponse,
} from "../WorkspaceAccessibility";

import { getWorkspaceAccessibilityQueryKey } from "./useWorkspaceAccessibility";

interface UpdateWorkspaceAccessibilityParams {
  workspaceId: string;
  accessibility: WorkspaceAccessibilityLevel;
}

/**
 * Hook to update the accessibility of a workspace and all its datasets.
 *
 * On success, invalidates the workspace accessibility query cache to refetch
 * the updated state.
 *
 * @returns React Query mutation for updating workspace accessibility
 */
export function useUpdateWorkspaceAccessibility() {
  const { workspaceService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    WorkspaceAccessibilityResponse,
    Error,
    UpdateWorkspaceAccessibilityParams
  >({
    mutationFn: async ({ workspaceId, accessibility }) => {
      return workspaceService.updateWorkspaceAccessibility(workspaceId, {
        accessibility,
      });
    },
    onSuccess: (data, { workspaceId }) => {
      // Update the cache with the new accessibility data
      queryClient.setQueryData(
        getWorkspaceAccessibilityQueryKey(workspaceId),
        data,
      );
    },
  });
}
